import React, { useContext, useEffect, useState, Suspense } from 'react';
import lazy from '../../helpers/lazyWithRetry';
import { Route, Routes, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import contents from '../../routes/contentRoutes';
import { UserType } from '../../common/data/dummyRolesData';
import RoleProtectedRoute from '../../components/auth/RoleProtectedRoute';
import AuthContext from '../../contexts/authContext';
import Loading from '../../components/Loading';

const PAGE_404 = lazy(() => import('../../pages/presentation/auth/Page404'));
const UNAUTHORIZED = lazy(() => import('../../pages/presentation/auth/Unauthorized'));
// const LOGIN = lazy(() => import('../../pages/presentation/auth/Login'));

/**
 * Alliance US-10 / Flow step 6-7: Captures the invitation token from the URL
 * (/auth/register?invitation=xxx) and stores it in localStorage before the
 * MSAL auth flow begins. After authentication completes, App.tsx reads the
 * stored token and redirects the user to /talent/alliance/pending.
 */
const ALLIANCE_INVITATION_TOKEN_KEY = 'ot_alliance_invitation_token';

const InvitationTokenCapture: React.FC = () => {
	const [searchParams] = useSearchParams();

	useEffect(() => {
		const token = searchParams.get('invitation');
		if (token) {
			localStorage.setItem(ALLIANCE_INVITATION_TOKEN_KEY, token);
		}
	}, [searchParams]);

	// After saving the token, redirect to root which triggers MSAL login
	return <Navigate to='/' replace />;
};

const ContentRoutes = () => {
	const { isAuthenticated, isLoading, isBootstrapped, userData } = useContext(AuthContext);
	const location = useLocation();
	const userRole = userData?.role || null;
	const [showUnauthorized, setShowUnauthorized] = useState(false);

	// Role-based URL protection
	useEffect(() => {
		if (isLoading || !isAuthenticated || !userRole || !isBootstrapped) return;

		const path = location.pathname;

		// Allow /talents/:id for both user types (public talent profiles)
		if (path.startsWith('/talents/')) {
			setShowUnauthorized(false);
			return;
		}

		if (userRole === UserType.COMPANY && path.startsWith('/talent')) {
			setShowUnauthorized(true);
			return;
		}

		if (userRole === UserType.FREELANCE && path.startsWith('/company')) {
			setShowUnauthorized(true);
			return;
		}

		setShowUnauthorized(false);
	}, [isLoading, isAuthenticated, isBootstrapped, userRole, location.pathname]);

	// Blocca il routing finché il bootstrap non è completo
	if (isLoading || !isBootstrapped) {
		return (
			<div id='app-loading'>
				<Loading />
			</div>
		);
	}

	// Mostra pagina "Unauthorized" se il controllo del ruolo fallisce
	if (showUnauthorized) {
		return <UNAUTHORIZED />;
	}

	return (
		<Suspense fallback={<Loading />}>
			<Routes>
				{/* Root path - let App/App.tsx handle redirection with proper organization checks */}
				<Route
					path='/'
					element={
						<>
							{console.log(
								'[ContentRoutes] Rendering root path (/), showing Loading',
							)}
							<Loading />
						</>
					}
				/>
				<Route path='/login' element={<Navigate to='/' replace />} />
				<Route path='/auth/login' element={<Navigate to='/' replace />} />
				<Route path='/auth-pages/login' element={<Navigate to='/' replace />} />

				{/* Alliance US-10 / Flow step 6-7: Handle invitation link for unregistered users.
				    Saves the invitation token to localStorage before MSAL auth redirect,
				    so we can redirect to /talent/alliance/pending after registration. */}
				<Route path='/auth/register' element={<InvitationTokenCapture />} />

				{/* Mappatura delle route dai contenuti */}
				{contents.map((page) => {
					if (!page.path) return null;

					if (page.path.startsWith('company/') || page.path.startsWith('/company')) {
						return (
							<Route
								key={page.path}
								path={page.path}
								element={
									<RoleProtectedRoute
										allowedRoles={[UserType.COMPANY]}
										unauthorizedComponent={<UNAUTHORIZED />}>
										{page.element}
									</RoleProtectedRoute>
								}
							/>
						);
					}

					if (page.path.startsWith('talent/') || page.path.startsWith('/talent')) {
						return (
							<Route
								key={page.path}
								path={page.path}
								element={
									<RoleProtectedRoute
										allowedRoles={[UserType.FREELANCE]}
										unauthorizedComponent={<UNAUTHORIZED />}>
										{page.element}
									</RoleProtectedRoute>
								}
							/>
						);
					}

					// Altre route (pubbliche o solo con autenticazione)
					return <Route key={page.path} {...page} />;
				})}

				{/* 404 */}
				<Route path='/auth-pages/404' element={<PAGE_404 />} />
				<Route path='*' element={<Navigate to='/auth-pages/404' replace />} />
			</Routes>
		</Suspense>
	);
};

export default ContentRoutes;
